import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateCampaignRequest,
  LaunchRequest,
  UpdateCampaignRequest,
} from '@outreach/contracts';
import { api } from '@/lib/api';

export function useCampaigns() {
  return useQuery({ queryKey: ['campaigns'], queryFn: () => api.campaigns.list() });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.campaigns.get(id!),
    enabled: !!id,
  });
}

/** The immutable run + counts; polled while the campaign is actively sending. */
export function useCampaignRun(id: string | undefined, active: boolean) {
  return useQuery({
    queryKey: ['campaign-run', id],
    queryFn: () => api.campaigns.run(id!),
    enabled: !!id,
    refetchInterval: active ? 2000 : false,
  });
}

/** Per-run dispatch + engagement report; polled while active. */
export function useCampaignReport(id: string | undefined, active: boolean) {
  return useQuery({
    queryKey: ['campaign-report', id],
    queryFn: () => api.campaigns.report(id!),
    enabled: !!id,
    refetchInterval: active ? 3000 : false,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCampaignRequest) => api.campaigns.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCampaignRequest) => api.campaigns.update(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaign', id] });
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

/** One hook for all lifecycle transitions; the action is chosen per call. */
export function useCampaignAction(id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['campaign', id] });
    void qc.invalidateQueries({ queryKey: ['campaign-run', id] });
    void qc.invalidateQueries({ queryKey: ['campaigns'] });
  };
  return {
    launch: useMutation({
      mutationFn: (body: LaunchRequest) => api.campaigns.launch(id, body),
      onSuccess: invalidate,
    }),
    pause: useMutation({ mutationFn: () => api.campaigns.pause(id), onSuccess: invalidate }),
    resume: useMutation({ mutationFn: () => api.campaigns.resume(id), onSuccess: invalidate }),
    cancel: useMutation({ mutationFn: () => api.campaigns.cancel(id), onSuccess: invalidate }),
    clone: useMutation({ mutationFn: () => api.campaigns.clone(id), onSuccess: invalidate }),
  };
}
